/** @file useArtModel: what the Art screen shows, derived from the store: parts, slots, states, warnings. */
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import type { ImageSlot } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { useMemo } from 'react';
import { useEditor } from '../../state/useEditor';
import type { ArtDeps, ArtPart, ArtSlot, SlotState, SlotViewAngles } from './artTypes';
import { partsOf } from './partsOf';
import { slotsOfPart } from './slotsOfPart';
import { slotWarnings, type SlotWarning } from './slotWarnings';
import { useSlotViews } from './useSlotViews';

/** The Art screen's view of the puppet; `slots` are the shown part's, `selected` the selected slot among them. */
export type ArtModel = {
  puppetId: string | null; puppet: Puppet | null; all: ImageSlot[]; parts: ArtPart[]; drawable: string | null;
  slots: ArtSlot[]; selected: ArtSlot | null; states: ReadonlyMap<string, SlotState>; warnings: SlotWarning[];
  views: ReadonlyMap<string, SlotViewAngles>; filled: number;
};

const NO_SLOTS: ImageSlot[] = [];

/** Filled slots some non-EMPTY_SLOT warning names show `warning`; the rest show their status. */
function statesOf(all: ImageSlot[], warnings: SlotWarning[]): Map<string, SlotState> {
  const warned = new Set(warnings.filter((w) => w.warning.code !== 'EMPTY_SLOT').map((w) => w.slot));
  return new Map(all.map((s) => [s.key, s.status === 'filled' && warned.has(s.key) ? 'warning' : s.status]));
}

/** Reads the document, lint and selection; the shown part is `selection.drawable` when it has slots, else the first. */
export function useArtModel(deps: Pick<ArtDeps, 'slots'>): ArtModel {
  const puppetId = useEditor((s) => s.puppet.id);
  const puppet = useEditor((s) => s.puppet.doc?.puppet ?? null);
  const lint = useEditor((s) => s.puppet.lint);
  const selection = useEditor((s) => s.selection);
  const all = useMemo(() => (puppet === null ? NO_SLOTS : deriveSlots(puppet)), [puppet]);
  const views = useSlotViews(deps, puppetId, useMemo(() => all.map((s) => s.key).join('|'), [all]));
  const parts = useMemo(() => (puppet === null ? [] : partsOf(puppet)), [puppet]);
  const drawable = parts.some((p) => p.drawable === selection.drawable) ? selection.drawable : (parts[0]?.drawable ?? null);
  const slots = useMemo(
    () => (drawable === null ? [] : slotsOfPart(all.map((s) => ({ ...s, view: views.get(s.key) })), drawable)),
    [all, views, drawable],
  );
  const warnings = useMemo(() => (puppet === null ? [] : slotWarnings(lint, puppet)), [lint, puppet]);
  const states = useMemo(() => statesOf(all, warnings), [all, warnings]);
  const selected = slots.find((s) => s.key === selection.slot) ?? null;
  const filled = all.filter((s) => s.status === 'filled').length;
  return { puppetId, puppet, all, parts, drawable, slots, selected, states, warnings, views, filled };
}
